<template>
  <div v-if="showAiButton" class="ai-assistant-wrapper">
    <!-- FAB Button -->
    <v-btn
      fab
      small
      class="ai-content-fab"
      :style="{ bottom: fabBottom }"
      @click="toggleChat"
    >
      <v-icon color="white" size="22">{{ mdiCreation }}</v-icon>
    </v-btn>

    <!-- Chat Overlay -->
    <v-card
      v-if="showChat"
      class="ai-content-chat"
      :style="{ bottom: chatBottom }"
    >
      <!-- Header -->
      <div class="ai-content-chat-header">
        <div class="ai-content-chat-header-icon">
          <v-icon color="primary" size="14">{{ mdiCreation }}</v-icon>
        </div>
        <span class="subtitle-2 font-weight-bold primary--text ml-2 text-truncate" style="flex: 1">
          {{ pageName || 'Assistente IA' }}
        </span>
        <v-btn
          v-if="conversationId"
          icon
          x-small
          @click="openFullConversation"
          class="mr-1"
        >
          <v-icon size="18" color="primary">{{ mdiOpenInNew }}</v-icon>
        </v-btn>
        <v-btn icon x-small @click="showChat = false">
          <v-icon size="18" color="grey">{{ mdiClose }}</v-icon>
        </v-btn>
      </div>

      <!-- Messages Area -->
      <div class="ai-content-chat-messages" ref="messagesArea">
        <!-- Welcome state -->
        <div v-if="!messages.length && !sending" class="ai-content-chat-welcome">
          <v-icon color="primary" size="24">{{ mdiCreation }}</v-icon>
          <div class="caption grey--text text--darken-1 mt-2 text-center">
            Pergunte algo sobre o conteúdo desta página
          </div>
        </div>

        <!-- Messages -->
        <div
          v-for="(msg, index) in messages"
          :key="index"
          class="mb-3"
          :class="{
            'd-flex justify-end': msg.role === 'user',
            'd-flex justify-start': msg.role !== 'user',
          }"
        >
          <!-- Assistant avatar -->
          <div
            v-if="msg.role !== 'user'"
            class="ai-mini-avatar mr-1 flex-shrink-0"
          >
            <v-icon color="primary" size="10">{{ mdiCreation }}</v-icon>
          </div>

          <!-- User bubble -->
          <div v-if="msg.role === 'user'" class="ai-mini-bubble ai-mini-bubble--user">
            <div
              v-if="msg.attachments && msg.attachments.length"
              class="ai-mini-attach-list"
            >
              <div
                v-for="(att, aidx) in msg.attachments"
                :key="aidx"
                class="ai-mini-attach-chip"
                :title="att.name"
              >
                <v-icon size="12" color="white" class="mr-1">{{ iconForMime(att.mimeType) }}</v-icon>
                <span class="caption text-truncate">{{ att.name }}</span>
              </div>
            </div>
            <div v-if="msg.content" class="caption white--text" style="white-space: pre-wrap">{{ msg.content }}</div>
          </div>

          <!-- Assistant bubble -->
          <div v-else class="ai-mini-bubble ai-mini-bubble--assistant">
            <div class="ai-mini-markdown">
              <vue-markdown :source="getProcessedContent(msg)" :html="false" />
            </div>
          </div>
        </div>

        <!-- Typing indicator -->
        <div v-if="isProcessing" class="d-flex justify-start mb-3">
          <div class="ai-mini-avatar mr-1 flex-shrink-0">
            <v-icon color="primary" size="10">{{ mdiCreation }}</v-icon>
          </div>
          <div class="ai-mini-bubble ai-mini-bubble--assistant">
            <div class="chat-typing">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>

        <!-- Error state -->
        <div v-if="hasError" class="ai-mini-error mb-3">
          <div class="caption error--text">Erro ao processar. Tente novamente.</div>
          <v-btn x-small rounded color="error" class="text-none mt-1" @click="retryLastMessage">
            Tentar novamente
          </v-btn>
        </div>

        <div ref="messagesEnd" style="height: 1px"></div>
      </div>

      <!-- Input -->
      <div class="ai-content-chat-input">
        <ai-attachment-picker
          v-model="files"
          :disabled="sending || isProcessing"
        />
        <v-textarea
          v-model="textModel"
          placeholder="Pergunte sobre este conteúdo..."
          :loading="sending"
          :disabled="sending || isProcessing"
          auto-grow
          no-resize
          rows="1"
          row-height="14"
          class="ai-mini-input"
          hide-details
          solo
          flat
          @keydown.enter.exact.prevent="sendMsg"
        >
          <template v-slot:append>
            <v-btn
              :loading="sending"
              :disabled="!canSend"
              @click="sendMsg"
              fab
              color="primary"
              x-small
              class="chat-send-btn"
            >
              <v-icon color="white" size="16">{{ mdiArrowUp }}</v-icon>
            </v-btn>
          </template>
        </v-textarea>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mdiCreation, mdiArrowUp, mdiClose, mdiOpenInNew, mdiFileDocumentOutline, mdiFilePdfBox, mdiImageOutline, mdiFileTableOutline } from "@mdi/js";
import { mapGetters, mapActions } from "vuex";
import VueMarkdown from "vue-markdown";
import { transformCitations } from "@/constants/citationRoutes";
import AiAttachmentPicker from "@/components/AiAttachmentPicker.vue";

const MAX_CONTEXT_LENGTH = 2500;

function extractSectionsText(sections) {
  if (!Array.isArray(sections)) return '';
  var parts = [];
  for (var i = 0; i < sections.length; i++) {
    var s = sections[i];
    if (s.text) parts.push(s.text);
    if (s.description) parts.push(s.description);
    if (Array.isArray(s.paragraphs)) {
      parts.push(s.paragraphs.join(' '));
    }
    if (Array.isArray(s.sections)) {
      parts.push(extractSectionsText(s.sections));
    }
    if (Array.isArray(s.objects)) {
      for (var j = 0; j < s.objects.length; j++) {
        var obj = s.objects[j];
        if (obj.text) parts.push(obj.text);
        if (obj.description) parts.push(obj.description);
      }
    }
  }
  return parts.filter(Boolean).join('\n');
}

export default {
  name: "AiContentAssistant",
  components: {
    "vue-markdown": VueMarkdown,
    "ai-attachment-picker": AiAttachmentPicker,
  },
  props: {
    contextData: { type: Object, required: true },
    pageType: { type: String, required: true },
    pageName: { type: String, default: '' },
    bottomOffset: { type: Number, default: 24 },
  },
  data() {
    return {
      showChat: false,
      textModel: "",
      sending: false,
      messages: [],
      files: [],
      lastUserFiles: [],
      conversationId: null,
      pollingInterval: null,
      lastUserMessage: null,
      mdiCreation,
      mdiArrowUp,
      mdiClose,
      mdiOpenInNew,
    };
  },
  computed: {
    ...mapGetters("general", ["toggles"]),
    ...mapGetters("topics", ["conversation"]),
    showAiButton() {
      return false;
      return this.toggles && this.toggles['ai_search'];
    },
    fabBottom() {
      return this.bottomOffset + 'px';
    },
    chatBottom() {
      return (this.bottomOffset + 56) + 'px';
    },
    contextText() {
      return this.extractTextContent(this.contextData, this.pageType);
    },
    isProcessing() {
      return this.conversation && this.conversation.status === 'processing' && this.conversationId;
    },
    hasError() {
      return this.conversation && this.conversation.status === 'error' && this.conversationId;
    },
    canSend() {
      if (this.isProcessing || this.sending) return false;
      var hasText = !!(this.textModel && this.textModel.trim());
      var hasFiles = Array.isArray(this.files) && this.files.length > 0;
      return hasText || hasFiles;
    },
  },
  watch: {
    isProcessing(val) {
      if (val) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },
    conversation(val) {
      if (val && val.messages && this.conversationId) {
        this.messages = [...val.messages];
        this.$nextTick(() => { this.scrollToBottom(); });
      }
    },
  },
  mounted() {
    this.addBottomPadding();
  },
  beforeDestroy() {
    this.stopPolling();
    this.removeBottomPadding();
    if (this.conversationId) {
      this.$store.commit('topics/clearConversation');
    }
  },
  methods: {
    ...mapActions("general", ["routePathRedirect"]),
    ...mapActions("topics", ["sendMessage", "pollConversation"]),

    extractTextContent(data, type) {
      var text = '';
      switch (type) {
        case 'item':
          text = [data.name, extractSectionsText(data.sections)].filter(Boolean).join('\n');
          break;
        case 'itemContent':
          text = [data.item ? data.item.name : '', extractSectionsText(data.topicsSections || (data.item ? data.item.sections : []))].filter(Boolean).join('\n');
          break;
        case 'drug':
          text = [data.name, data.description, extractSectionsText(data.info), extractSectionsText(data.attention)].filter(Boolean).join('\n');
          break;
        case 'dose':
          text = [data.drug ? data.drug.name : '', data.dose ? data.dose.description : '', extractSectionsText(data.dose ? data.dose.sections : [])].filter(Boolean).join('\n');
          break;
        case 'product':
          text = [data.name, data.brand, Array.isArray(data.animals) ? 'Animais: ' + data.animals.join(', ') : '', Array.isArray(data.classes) ? 'Classes: ' + data.classes.map(function(c) { return c.name || c; }).join(', ') : ''].filter(Boolean).join('\n');
          break;
        case 'clinicalHandout':
          text = [data.name, extractSectionsText(data.sections)].filter(Boolean).join('\n');
          break;
        case 'drugsInteractions':
          var drugNames = Array.isArray(data.drugs) ? data.drugs.map(function(d) { return d.name; }).join(', ') : '';
          var interactionTexts = Array.isArray(data.interactions) ? data.interactions.map(function(i) { return (i.drug_1 || '') + ' + ' + (i.drug_2 || '') + ': ' + (i.description || i.severity || ''); }).join('\n') : '';
          text = ['Medicamentos: ' + drugNames, interactionTexts].filter(Boolean).join('\n');
          break;
        case 'ddx':
          text = ['Espécie: ' + (data.species || ''), 'Sinais clínicos: ' + (Array.isArray(data.listOfSigns) ? data.listOfSigns.map(function(s) { return s.name || s; }).join(', ') : ''), 'Diagnósticos: ' + (Array.isArray(data.diagnoses) ? data.diagnoses.map(function(d) { return d.name || d; }).join(', ') : '')].filter(Boolean).join('\n');
          break;
        case 'references':
          text = [data.reference ? data.reference.name : '', data.reference ? data.reference.magazine : '', data.reference ? ('Ano: ' + data.reference.year) : '', data.drug ? data.drug.name : ''].filter(Boolean).join('\n');
          break;
        default:
          text = JSON.stringify(data).substring(0, MAX_CONTEXT_LENGTH);
      }
      return text.substring(0, MAX_CONTEXT_LENGTH);
    },

    toggleChat() {
      this.showChat = !this.showChat;
      if (this.showChat) {
        this.$nextTick(() => {
          var input = this.$el.querySelector('.ai-mini-input textarea');
          if (input) input.focus();
        });
      }
    },

    async sendMsg() {
      if (!this.canSend) return;
      var message = (this.textModel && this.textModel.trim()) || "";
      var filesToSend = Array.isArray(this.files) ? this.files.slice() : [];
      if (!message && !filesToSend.length) return;

      this.lastUserMessage = message;
      this.lastUserFiles = filesToSend.slice();
      this.textModel = "";
      this.sending = true;

      var attachmentsMeta = filesToSend.map(function (f) {
        return { name: f.name, mimeType: f.mimeType, size: f.size };
      });
      this.messages.push({ role: 'user', content: message, attachments: attachmentsMeta, timestamp: Date.now() });
      this.files = [];
      this.$nextTick(() => { this.scrollToBottom(); });

      // Clear previous conversation state if starting fresh
      if (!this.conversationId) {
        this.$store.commit('topics/clearConversation');
      }

      var result = await this.sendMessage({
        conversationId: this.conversationId,
        message: message,
        context: this.contextText,
        files: filesToSend,
      });

      this.sending = false;

      if (result && result.conversationId) {
        this.conversationId = result.conversationId;
        this.startPolling();
      }
    },

    async retryLastMessage() {
      if (!this.lastUserMessage && !(this.lastUserFiles && this.lastUserFiles.length)) return;
      this.textModel = this.lastUserMessage || "";
      this.files = Array.isArray(this.lastUserFiles) ? this.lastUserFiles.slice() : [];
      await this.sendMsg();
    },

    iconForMime(mime) {
      if (!mime) return mdiFileDocumentOutline;
      if (mime.indexOf('image/') === 0) return mdiImageOutline;
      if (mime === 'application/pdf') return mdiFilePdfBox;
      if (mime === 'text/csv') return mdiFileTableOutline;
      return mdiFileDocumentOutline;
    },

    startPolling() {
      this.stopPolling();
      this.pollNow();
      this.pollingInterval = setInterval(() => {
        this.pollNow();
      }, 5000);
    },

    async pollNow() {
      if (!this.conversationId) return;
      var data = await this.pollConversation({ id: this.conversationId });
      if (data && data.status !== 'processing') {
        this.stopPolling();
      }
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    openFullConversation() {
      this.showChat = false;
      this.routePathRedirect({ path: '/ai-conversations/' + this.conversationId });
    },

    getProcessedContent(msg) {
      var content = msg.content || "";
      content = content.replace(/\b(?:cite)?(?:turn\d+(?:file|search)\d+)+\b/g, '');
      content = content.replace(/\?utm_soa:[^\s)\]]+/g, '');
      content = content.replace(/(\[[^\]]+\]\([^)\]]+)\]\)/g, '$1)');
      content = content.replace(/(\[[^\]]+\]\([^)\]]+)\](?=[\s.,;:!?)]|$)/g, '$1)');

      var citations = msg.citations || [];
      var fileMap = {};
      for (var i = 0; i < citations.length; i++) {
        var c = citations[i];
        if (c.file_id && c.filename) {
          fileMap[c.file_id] = c.filename;
        }
      }
      content = content.replace(/\[VetAlfa:(file-[^\]]+)\]/gi, function(_, fileId) {
        var filename = fileMap[fileId.trim()];
        return filename ? "[VetAlfa:" + filename + "]" : "";
      });
      content = transformCitations(content);
      content = content.replace(/\[VetAlfa:[^\]]*\]/gi, "");
      content = content.replace(/ {2,}/g, " ").replace(/\n{3,}/g, "\n\n");
      return content;
    },

    scrollToBottom() {
      if (this.$refs.messagesArea) {
        this.$refs.messagesArea.scrollTop = this.$refs.messagesArea.scrollHeight;
      }
    },

    addBottomPadding() {
      var parent = this.$el && this.$el.parentElement;
      if (parent) {
        var screenContent = parent.querySelector('[ref="screenContent"]') || parent.querySelector('div[ref]');
        if (screenContent) {
          screenContent.style.paddingBottom = '80px';
        } else {
          parent.style.paddingBottom = '80px';
        }
      }
    },

    removeBottomPadding() {
      var parent = this.$el && this.$el.parentElement;
      if (parent) {
        var screenContent = parent.querySelector('[ref="screenContent"]') || parent.querySelector('div[ref]');
        if (screenContent) {
          screenContent.style.paddingBottom = '';
        } else {
          parent.style.paddingBottom = '';
        }
      }
    },
  },
};
</script>

<style>
/* ── FAB Button ── */
.ai-content-fab {
  position: fixed !important;
  right: 16px;
  z-index: 99;
  background: linear-gradient(135deg, #193153 0%, #00728a 100%) !important;
  box-shadow: 0 4px 16px rgba(25, 49, 83, 0.3) !important;
  transition: transform 0.15s ease !important;
}
.ai-content-fab:hover {
  transform: scale(1.08);
}

/* ── Chat Overlay ── */
.ai-content-chat {
  position: fixed !important;
  right: 16px;
  width: 340px;
  max-height: 60vh;
  z-index: 100;
  border-radius: 16px !important;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15) !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
@media (max-width: 400px) {
  .ai-content-chat {
    width: calc(100vw - 32px);
    left: 16px;
    right: 16px;
  }
}

/* ── Chat Header ── */
.ai-content-chat-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
  background: #fafbfc;
}
.ai-content-chat-header-icon {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background: rgba(25, 49, 83, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Messages Area ── */
.ai-content-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  min-height: 120px;
  max-height: calc(60vh - 120px);
}

/* ── Welcome ── */
.ai-content-chat-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
}

/* ── Mini Bubbles ── */
.ai-mini-avatar {
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  background: rgba(25, 49, 83, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
}
.ai-mini-bubble {
  max-width: 85%;
  padding: 8px 12px;
}
.ai-mini-bubble--user {
  background-color: #193153;
  color: white;
  border-radius: 12px 12px 4px 12px;
  box-shadow: 0 1px 3px rgba(25, 49, 83, 0.18);
}
.ai-mini-attach-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
}
.ai-mini-attach-chip {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 2px 8px;
  max-width: 180px;
}
.ai-mini-attach-chip .caption {
  color: #fff;
  font-size: 11px;
}
.ai-mini-bubble--assistant {
  background-color: #f5f6fa;
  border-radius: 12px 12px 12px 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

/* ── Mini Markdown ── */
.ai-mini-markdown {
  font-size: 13px;
  line-height: 1.5;
  color: #212121;
}
.ai-mini-markdown p {
  margin-bottom: 6px;
}
.ai-mini-markdown p:last-child {
  margin-bottom: 0;
}
.ai-mini-markdown h1,
.ai-mini-markdown h2,
.ai-mini-markdown h3 {
  margin-top: 8px;
  margin-bottom: 4px;
  font-weight: 600;
  font-size: 14px;
  color: #193153;
}
.ai-mini-markdown ul,
.ai-mini-markdown ol {
  padding-left: 16px;
  margin-bottom: 6px;
}
.ai-mini-markdown li {
  margin-bottom: 2px;
}
.ai-mini-markdown a {
  color: #00728a;
  text-decoration: none;
}
.ai-mini-markdown code {
  background: #eee;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 0.9em;
}

/* ── Mini Error ── */
.ai-mini-error {
  background: #fef2f2;
  border-radius: 8px;
  padding: 8px 12px;
  text-align: center;
}

/* ── Input Area ── */
.ai-content-chat-input {
  border-top: 1px solid #eee;
  padding: 6px 8px;
  background: #fafbfc;
}
.ai-mini-input .v-input__slot {
  background-color: white !important;
  box-shadow: none !important;
  border: 1px solid #e0e0e0;
  border-radius: 16px !important;
  padding: 4px 6px 4px 14px !important;
  min-height: 36px !important;
  align-items: flex-end !important;
}
.ai-mini-input .v-text-field__slot textarea {
  padding: 4px 0 !important;
  line-height: 1.4 !important;
  font-size: 13px !important;
}
.ai-mini-input .v-input__append-inner {
  padding: 0;
  margin: 0 2px 4px 0;
  align-self: flex-end;
}
</style>
